import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Button,
  TableHead,
  IconButton,
  Switch,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

const API_URL = API_ENDPOINTS.REGIONS;

const RegionsListContent = () => {
  const [regions, setRegions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user } = useAuth();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error'
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
  });

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      const response = await axios.get(API_URL.BASE);
      const data = response.data;
      let regionsArray = [];
      if (Array.isArray(data)) {
        regionsArray = data;
      } else if (data && Array.isArray(data.regions)) {
        regionsArray = data.regions;
      }
      setRegions(regionsArray);
    } catch (error) {
      console.error("Error fetching regions:", error);
      setRegions([]);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = deleteDialog.id;
    setDeleteDialog({ open: false, id: null });
    try {
      await axios.delete(API_URL.DELETE(id));
      setRegions((prevRegions) => prevRegions.filter((region) => region.id !== id));
      setSnackbar({
        open: true,
        message: "Region deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      let userMessage = "Failed to delete region.";
      if (
        error.response?.data?.message &&
        error.response.data.message.includes("foreign key constraint fails")
      ) {
        userMessage =
          "Cannot delete this region because it is assigned to one or more users. Please reassign or remove those users first.";
      }
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null });
  };
  

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await axios.put(API_URL.PUBLISH(id), { publish: !currentStatus });
      setRegions((prevRegions) =>
        prevRegions.map((region) =>
          region.id === id ? { ...region, publish: !currentStatus } : region
        )
      );
    } catch (error) {
      console.error("Error updating publish status:", error);
    }
  };
  

  const filteredRegions = Array.isArray(regions)
    ? regions.filter((region) =>
        region.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", padding: "25px" }}>
      {/* <NavList /> */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "25px" }}>
          <input
            type="text"
            placeholder="Search Region..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "8px", width: "200px", borderRadius: "5px", border: "1px solid #ccc" }}
          />
          {user.permissions.includes("add-regions") && (
            <Link to="/ecommerce/create-region">
              <Button variant="outlined" color="primary">
                <AddIcon /> Add New Region
              </Button>
            </Link>
          )}
        </Box>

        <TableContainer className="custom-table" component={Paper}>
          <Table sx={{ minWidth: 1000 }} aria-label="Regions Table">
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Publish</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRegions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((region, index) => (
                  <TableRow key={region.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{region.name}</TableCell>
                    <TableCell>
                      <Switch
                        checked={region.publish}
                        onChange={() => handleTogglePublish(region.id, region.publish)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      {/* {user.permissions.includes("view-regions") && (
                        <Link to={`/ecommerce/regions-list/details/${region.id}`}>
                          <IconButton color="info">
                            <VisibilityIcon />
                          </IconButton>
                        </Link>
                      )} */}
                      {user.permissions.includes("edit-regions") && (
                        <Link to={`/ecommerce/edit-region/${region.id}`}>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("delete-regions") && (
                        <IconButton color="error" onClick={() => handleDeleteClick(region.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  count={filteredRegions.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Region</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this region?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default RegionsListContent;